import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box, Typography, Grid, IconButton,
    Paper, Button, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import imageCompression from 'browser-image-compression';

const ImageUpload = ({
    onFileSelect,
    value = [],
    multiple = false,
    variant = 'standard', // 'standard' | 'profile' | 'property'
    maxFiles = 10,
    maxSize = 5242880, // 5MB
    title = '写真アップロード',
    uploadPath = 'images' // Firebase Storageのパス
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    // variantに基づいて最大ファイル数を決定
    const actualMaxFiles = variant === 'profile' ? 1 :
        variant === 'property' ? 5 :
            maxFiles;

    // 許可されるファイル形式を定義
    const acceptedFormats = {
        'image/jpeg': ['.jpeg', '.jpg'],
        'image/jpg': ['.jpg'],
        'image/gif': ['.gif'],
        'image/png': ['.png']
    };

    // 許可形式の文字列表示（エラーメッセージ用）
    const acceptedFormatsText = 'JPEG/JPG/GIF/PNG形式';

    // ファイル選択をリセットする関数
    const resetFileInput = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleUpload = useCallback(async (file) => {
        try {
            // ファイル形式のチェック
            if (!['image/jpeg', 'image/jpg', 'image/gif', 'image/png'].includes(file.type)) {
                throw new Error(`サポートされていないファイル形式です。${acceptedFormatsText}のみが許可されています`);
            }

            // 画像圧縮のオプション設定
            const options = {
                maxSizeMB: 1, // 最大サイズを1MBに設定
                maxWidthOrHeight: 1920, // 幅または高さを1920pxに制限
                useWebWorker: true
            };
            // 画像を圧縮
            const compressedFile = await imageCompression(file, options);

            // ファイル名をサニタイズ
            const sanitizedFileName = compressedFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
            const storageRef = ref(storage, `${uploadPath}/${Date.now()}_${sanitizedFileName}`);

            // 圧縮された画像をFirebase Storageにアップロード
            await uploadBytes(storageRef, compressedFile);
            return getDownloadURL(storageRef);
        } catch (error) {
            console.error('File upload error:', error);
            throw error;
        }
    }, [uploadPath, acceptedFormatsText]);

    const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
        // エラー処理を強化 - 拒否されたファイルの処理
        if (rejectedFiles && rejectedFiles.length > 0) {
            const rejectedFile = rejectedFiles[0];
            if (rejectedFile.errors) {
                const error = rejectedFile.errors[0];
                if (error.code === 'file-invalid-type') {
                    setError(`サポートされていないファイル形式です。${acceptedFormatsText}のみが許可されています`);
                } else if (error.code === 'file-too-large') {
                    setError(`ファイルサイズが大きすぎます。最大${maxSize / 1024 / 1024}MBまでのファイルを選択してください`);
                } else {
                    setError('ファイルのアップロードに失敗しました');
                }
            }
            return;
        }

        if (isUploading || !acceptedFiles.length) return;

        // 最大ファイル数のチェックを追加
        if (multiple && (value.length + acceptedFiles.length) > actualMaxFiles) {
            setError(`写真は最大${actualMaxFiles}枚までアップロードできます`);
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            // プロフィール写真の場合は最新の1枚のみを使用
            if (variant === 'profile') {
                const url = await handleUpload(acceptedFiles[0]);
                resetFileInput();
                onFileSelect(url);
                return;
            }

            // 物件写真の場合
            if (variant === 'property') {
                const newFiles = acceptedFiles.slice(0, actualMaxFiles - value.length);
                const urls = await Promise.all(newFiles.map(handleUpload));
                resetFileInput();
                onFileSelect([...value, ...urls]);
                return;
            }

            // その他の場合（標準の動作）
            const file = multiple ? acceptedFiles : acceptedFiles[0];
            if (!multiple) {
                const url = await handleUpload(file);
                resetFileInput();
                onFileSelect(url);
                return;
            }

            const urls = await Promise.all(acceptedFiles.map(handleUpload));
            resetFileInput();
            onFileSelect([...value, ...urls]);

        } catch (error) {
            console.error('Upload error:', error);
            setError(error.message || '画像のアップロードに失敗しました');
        } finally {
            setIsUploading(false);
        }
    }, [multiple, handleUpload, isUploading, onFileSelect, resetFileInput, value, variant, actualMaxFiles, maxSize, acceptedFormatsText]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: acceptedFormats,
        maxSize,
        multiple: variant !== 'profile', // プロフィール写真の場合は常に単一ファイル
        maxFiles: actualMaxFiles,
        disabled: isUploading || (multiple && value.length >= actualMaxFiles),
        preventDropOnDocument: true,
        noClick: isUploading,
    });

    const handleDelete = (index) => {
        if (multiple) {
            const newImages = value.filter((_, i) => i !== index);
            onFileSelect(newImages);
        } else {
            onFileSelect(null);
        }
    };

    // プロフィール画像用のレイアウト
    if (variant === 'profile') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {value && (
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            position: 'relative',
                            mb: 2
                        }}
                    >
                        <Box
                            component="img"
                            src={value}
                            alt="プロフィール画像"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                        {!isUploading && (
                            <IconButton
                                onClick={() => handleDelete(0)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                                }}
                            >
                                <DeleteIcon sx={{ color: 'white' }} />
                            </IconButton>
                        )}
                    </Box>
                )}
                <Box
                    {...getRootProps()}
                    sx={{
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        '&:hover': { opacity: 0.8 }
                    }}
                >
                    <Button
                        variant="contained"
                        component="span"
                        disabled={isUploading}
                        startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    >
                        {isUploading ? 'アップロード中...' : 'プロフィール画像を選択'}
                    </Button>
                    <input {...getInputProps()} />
                </Box>
                {error && (
                    <Typography color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
            </Box>
        );
    }

    // 物件写真用のレイアウト
    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                {title}
            </Typography>
            {variant === 'property' && (
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                    残り{actualMaxFiles - value.length}枚まで追加できます
                </Typography>
            )}

            <Paper
                {...getRootProps()}
                sx={{
                    p: 3,
                    mb: 3,
                    textAlign: 'center',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    cursor: 'pointer'
                }}
            >
                <input {...getInputProps()} />
                {isUploading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography>
                            {isDragActive
                                ? 'ここにファイルをドロップ'
                                : '写真をドラッグ＆ドロップ、またはクリックしてアップロード'
                            }
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            最大{maxSize / 1024 / 1024}MBまで、{acceptedFormatsText}
                        </Typography>
                    </>
                )}
            </Paper>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {multiple && Array.isArray(value) && (
                <Grid container spacing={2}>
                    {value.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                                sx={{
                                    position: 'relative',
                                    paddingTop: '75%',
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    component="img"
                                    src={image}
                                    alt={`写真 ${index + 1}`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                                <IconButton
                                    onClick={() => handleDelete(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                                    }}
                                >
                                    <DeleteIcon sx={{ color: 'white' }} />
                                </IconButton>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default ImageUpload; 